import { defineStore } from "pinia";
import { uniqBy, isNil } from "lodash-es";

export const jsonHeaders = {
  "Accept": "application/json",
  "Content-Type": "application/json",
};

/* Returns the array `tags`, replacing oldTag with newTag, its modified replacement. */
export const tagsWithUpdatedTag = (tags, oldTag, newTag) => {
  const replaced = tags.map((t) => {
    if (t.name === oldTag.name) {
      return newTag;
    } else {
      return t;
    }
  });

  return uniqBy(
    replaced.filter((t) => !isNil(t)),
    (t) => t.name
  );
};

export const useTagStore = ({
  modelName,
  tagModelName,
  tagPath,
  countTagsPath,
  tagsPath,
  bulkDestroyTagsPath,
  prototypeTagsPath,
  additionalActions = {},
}) => {
  /* requests for count that are in-flight */
  const countSet = new Set();

  const store = defineStore(`tagStore.${modelName}`, {
    state: () => {
      return {
        userId: null,
        loaded: false,
        allTags: [],
      };
    },

    getters: {
      getByName: (state) => (name) => state.allTags.find((tag) => tag.name === name),
    },

    actions: {
      ...additionalActions,

      setUserId(userId) {
        this.userId = userId;
      },

      async loadAllTags() {
        if (this.loaded) {
          return;
        }

        const response = await fetch(tagsPath(this.userId), {
          headers: { ...jsonHeaders },
        });

        const json = await response.json();
        this.allTags = json;
        this.loaded = true;
      },

      async createTag({ tag, modelId }) {
        const response = await fetch(tagsPath(this.userId), {
          method: "POST",
          headers: { ...jsonHeaders },
          body: JSON.stringify({
            [tagModelName]: {
              [`${modelName}_id`]: +modelId,
              name: tag.name,
              color: tag.color,
            },
          }),
        });

        const newTag = await response.json();

        /* Add it to the global list */
        if (!this.allTags.find((t) => t.name === newTag.name)) {
          this.allTags.push({ ...newTag, id: null });
        }

        return newTag;
      },

      async destroyTag({ tag }) {
        fetch(tagPath(this.userId, tag.id), {
          method: "DELETE",
          headers: { ...jsonHeaders },
        });
      },

      async destroyAllTags({ tag }) {
        fetch(bulkDestroyTagsPath(this.userId), {
          method: "POST",
          headers: { ...jsonHeaders },
          body: JSON.stringify({
            name: tag.name,
          }),
        });

        this.allTags = tagsWithUpdatedTag(this.allTags, tag, null);
      },

      /* Tag counts can be slow, we don't request them by default */
      async requestCount({ name, force = false } = {}) {
        const existingRecord = this.allTags.find((t) => t.name === name);

        /* if we've already got a count, return it */
        if (!force && !isNil(existingRecord?.count)) {
          return existingRecord.count;
        }

        /* if we're current fetching a count, abort */
        if (countSet.has(name)) {
          return;
        }

        countSet.add(name);

        const response = await fetch(countTagsPath(this.userId, { name }), {
          headers: { ...jsonHeaders },
        });

        const json = await response.json();
        const count = json.count;

        this.allTags = tagsWithUpdatedTag(this.allTags, existingRecord, {
          ...existingRecord,
          count,
        });

        countSet.delete(name);

        return count;
      },

      async refreshCounts() {
        for (const tag of this.allTags) {
          await this.requestCount({ name: tag.name, force: true });
        }
      },

      async updateTag({ oldTag, newTag }) {
        fetch(tagPath(this.userId, oldTag.id), {
          method: "PATCH",
          headers: { ...jsonHeaders },
          body: JSON.stringify({
            [tagModelName]: {
              name: newTag.name,
              color: newTag.color,
            },
          }),
        });

        newTag.count = oldTag.count;
        this.allTags = tagsWithUpdatedTag(this.allTags, oldTag, newTag);
      },

      async getPrototype({ name }) {
        const response = await fetch(prototypeTagsPath(this.userId, { name }), {
          headers: { ...jsonHeaders },
        });

        return response.json();
      },

      async updateTagByName({ name, tag }) {
        const prototypeTag = await this.getPrototype({ name });
        this.updateTag({ oldTag: prototypeTag, newTag: tag });
      },
    },
  });

  return store();
};
